import React, { useCallback, useMemo, useContext } from 'react';

import { InjectedIntl, injectIntl } from 'react-intl';

import { AppSwitcher } from '@atlaskit/atlassian-navigation';
import AppSwitcherIcon from '@atlaskit/icon/glyph/app-switcher';
// @ts-ignore TODO: fix noImplicitAny error here
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { GlobalItem } from '@atlaskit/navigation-next';
import { useFeature } from '@atlassian/bitbucket-features';
import { createDiscoverySdk } from '@atlassian/discovery-sdk';
import {
  Placement,
  usePostOfficeContext,
} from '@atlassian/post-office-frontend';
import RemoteSwitcherPlugin from '@atlassian/remote-module-loader/plugin-switcher';
import { RemoteComponent } from '@atlassian/remote-module-loader/remote-component';
import Switcher from '@atlassian/switcher';
import { Journeys, useCrossFlow } from '@atlassiansox/cross-flow-support';

import { Product } from '../../types';
import {
  defaultXFlowUrl,
  transformDeployEnvToPostOfficeEnvironment,
} from '../../utils/switcher';
import { HorizontalNavContext } from '../horizontal-nav/horizontal-nav-context';

import messages from './atlassian-switcher.i18n';
import { SwitcherContainer } from './atlassian-switcher.style';

const openXFlowUrl = (targetProduct: Product) => {
  const url = defaultXFlowUrl[targetProduct];
  if (url) {
    const win = window.open(url, '_blank');
    if (win) {
      win.focus();
    }
  }
};

export type DiscoverySurfaceComponent = (
  props: Record<string, any>
) => JSX.Element;

export type AtlassianSwitcherProps = {
  globalItemStyles?: (styles: {}) => {};
  isAtlassianSwitcherOpen: boolean;
  isLoggedIn: boolean;
  onToggleAtlassianSwitcher: (isOpen: boolean) => void;
};

type InjectedAtlassianSwitcherProps = AtlassianSwitcherProps & {
  intl: InjectedIntl;
};

type BaseSwitcherProps = {
  appearance: 'standalone' | 'drawer';
  discoverySurface: () => DiscoverySurfaceComponent | undefined;
  tenantId?: string;
};

type SwitcherComponentProps = React.ComponentProps<typeof Switcher>;

type DiscoveryExposureAttributes = {
  locale: string | undefined;
  ffEnableOverFiftyUsers: boolean | undefined;
  ffAllLocales: boolean | undefined;
};

type SwitcherDiscoveryExposureEvent = {
  actionSubject: 'section';
  action: 'viewed';
  actionSubjectId: 'switcherDiscoveryExposure';
  source: 'atlassianSwitcher';
  attributes: {
    locale: DiscoveryExposureAttributes['locale'];
    featureFlags: {
      ffEnableOverFiftyUsers: DiscoveryExposureAttributes['ffEnableOverFiftyUsers'];
      ffAllLocales: DiscoveryExposureAttributes['ffAllLocales'];
    };
  };
};

export const createSwitcherDiscoveryExposureEvent = ({
  locale,
  ffEnableOverFiftyUsers,
  ffAllLocales,
}: DiscoveryExposureAttributes): SwitcherDiscoveryExposureEvent => ({
  actionSubject: 'section',
  action: 'viewed',
  actionSubjectId: 'switcherDiscoveryExposure',
  source: 'atlassianSwitcher',
  attributes: {
    locale,
    featureFlags: {
      ffEnableOverFiftyUsers,
      ffAllLocales,
    },
  },
});

export const { DiscoverySurface, useDiscoverySdk } = createDiscoverySdk({
  context: {
    product: 'bitbucket',
  },
});

const assertBooleanFeatureFlagValue = (featureFlagValue: unknown) =>
  typeof featureFlagValue === 'boolean' ? featureFlagValue : false;

export const useDiscoverySdkFeatureFlags = (
  intl: {
    locale: string;
  },
  publishUiEvent: (event: Record<string, any>) => void,
  tenantId?: string
): {
  preloadSwitcherSurface: () => void;
  discoverySurface: () => DiscoverySurfaceComponent | undefined;
} => {
  const { locale } = intl;
  const { setContext } = useDiscoverySdk();

  const enableDiscoverySdkFlag = assertBooleanFeatureFlagValue(
    useFeature('bitbucket.app-recommendations.discovery-sdk', false)
  );

  const enableDiscoverySdkAllLocalesFlag = assertBooleanFeatureFlagValue(
    useFeature('bitbucket.app-recommendations.discovery-sdk.all-locale', false)
  );

  const isEnglishLocale = locale.startsWith('en');

  const isEnabled =
    (isEnglishLocale && enableDiscoverySdkFlag) ||
    (enableDiscoverySdkAllLocalesFlag && !isEnglishLocale);

  const discoverySurfaceWithExposureEvent = ():
    | DiscoverySurfaceComponent
    | undefined => {
    publishUiEvent(
      createSwitcherDiscoveryExposureEvent({
        locale,
        ffAllLocales: enableDiscoverySdkAllLocalesFlag,
        ffEnableOverFiftyUsers: enableDiscoverySdkFlag,
      })
    );

    return isEnabled ? (DiscoverySurface as () => JSX.Element) : undefined;
  };

  return {
    preloadSwitcherSurface: () => {
      setContext(c => ({
        ...c,
        switcherMenuHasBeenOpened: isEnabled,
        locale,
        workspaceId: tenantId,
      }));
    },
    discoverySurface: discoverySurfaceWithExposureEvent,
  };
};

export const BaseSwitcher: React.FC<BaseSwitcherProps> = React.memo(props => {
  const { appearance, discoverySurface, tenantId } = props;
  const crossFlow = useCrossFlow();
  const { environment } = useContext(HorizontalNavContext);
  const postOfficeContext = usePostOfficeContext();

  const remoteSwitcherPlugin = useMemo(() => {
    return new RemoteSwitcherPlugin({
      env: environment === 'production' ? 'production' : 'staging',
      variant: 'latest',
    });
  }, [environment]);

  const onTriggerXFlow = useCallback(
    (targetProduct: Product) => {
      if (crossFlow.isEnabled) {
        crossFlow.api.open({
          journey: Journeys.DECIDE,
          targetProduct,
          sourceComponent: 'atlassian-switcher',
          sourceContext: 'recommendation',
        });
      } else {
        openXFlowUrl(targetProduct);
      }
    },
    [crossFlow]
  );

  const onDiscoverMoreClicked = useCallback(() => {
    if (crossFlow.isEnabled) {
      crossFlow.api.open({
        journey: Journeys.DISCOVER,
        sourceComponent: 'atlassian-switcher',
        sourceContext: 'more',
      });
    }
  }, [crossFlow]);
  const isPostOfficeIntegrationEnabled = useFeature(
    'post-office-in-bitbucket-switcher',
    false
  );
  const switcherProps = useMemo<SwitcherComponentProps>(
    () => ({
      appearance,
      product: 'bitbucket',
      disableCustomLinks: true,
      triggerXFlow: onTriggerXFlow,
      onDiscoverMoreClicked,
      discoverySurface: discoverySurface(),
      discoverySection:
        postOfficeContext.locale?.startsWith('en') &&
        isPostOfficeIntegrationEnabled ? (
          <Placement
            placementId="app-switcher-discovery-section"
            env={transformDeployEnvToPostOfficeEnvironment(environment)}
            variant="latest"
            context={{ tenantId, ...postOfficeContext }}
          />
        ) : (
          undefined
        ),
    }),
    [
      appearance,
      onTriggerXFlow,
      onDiscoverMoreClicked,
      discoverySurface,
      isPostOfficeIntegrationEnabled,
      environment,
      tenantId,
      postOfficeContext,
    ]
  );

  return (
    <SwitcherContainer>
      <RemoteComponent<SwitcherComponentProps>
        remotePlugin={remoteSwitcherPlugin}
        analyticsChannel="navigation"
        ErrorFallback={<Switcher {...switcherProps} />}
        {...switcherProps}
      />
    </SwitcherContainer>
  );
});

export const SwitcherButton = injectIntl(
  ({
    onToggleAtlassianSwitcher,
    isHorizontalNavSwitcher,
    isSelected,
    intl,
  }: Omit<
    InjectedAtlassianSwitcherProps,
    'isAtlassianSwitcherOpen' | 'isLoggedIn'
  > & { isHorizontalNavSwitcher?: boolean; isSelected?: boolean }) => (
    <>
      {isHorizontalNavSwitcher ? (
        <AppSwitcher
          id="appSwitcher"
          testId="app-switcher"
          isSelected={isSelected}
          onClick={() => onToggleAtlassianSwitcher(true)}
          tooltip={intl.formatMessage(messages.tooltip)}
        />
      ) : (
        <GlobalItem
          id="appSwitcher"
          icon={AppSwitcherIcon}
          onClick={() => onToggleAtlassianSwitcher(true)}
          size="small"
          label={intl.formatMessage(messages.trigger)}
          tooltip={intl.formatMessage(messages.tooltip)}
        />
      )}
    </>
  )
);
